import { Check, ClipboardList, FolderIcon, ListTodo, Search } from "lucide-react";
import {
  folderLabel,
  isBriefFolder,
  isIssueAttachmentsFolder,
} from "@/domains/documents/utils/briefFolder";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { FileTypeIcon } from "@/components/ui/documents/file-type-icon";
import { VersionBadge } from "@/components/ui/documents/version-badge";
import { Skeleton } from "@/components/ui/skeleton";
import type { TreeFolder } from "@/domains/documents/types";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import type { RawDocument } from "./documentPickerTypes";

export interface DocumentPickerListState {
  selectedProjectId: string | null;
  projectsFailed: boolean;
  searchQuery: string;
  debouncedSearchQuery: string;
  selectedFolderId: string | null;
  subfolderDocumentsFailed: boolean;
  documentsFailed: boolean;
  searchTruncated: boolean;
  listLoading: boolean;
  filteredChildFolders: TreeFolder[];
  filteredDocuments: RawDocument[];
  selectedDocIds: ReadonlySet<string>;
  alreadyAttachedIds: readonly string[];
  folderBreadcrumbMap: ReadonlyMap<string, string>;
}

/**
 * The version chain behind one row. The picker owns the two ways a chain can be
 * resolved (in memory, or fetched on first open) so the list only has to ask.
 */
export interface DocumentPickerListVersions {
  /** Does this row head a chain worth a chevron? */
  canExpand: (document: RawDocument) => boolean;
  isExpanded: (document: RawDocument) => boolean;
  /** First fetch in flight — fetched path only. */
  isLoading: (document: RawDocument) => boolean;
  /** The superseded revisions, or `undefined` when not yet fetched. An empty
   *  array means they existed and are gone, which the row says out loud. */
  older: (document: RawDocument) => RawDocument[] | undefined;
  toggle: (document: RawDocument) => void;
}

export interface DocumentPickerListActions {
  setSearchQuery: (query: string) => void;
  retrySubfolderDocuments: () => void;
  retryDocuments: () => void;
  navigateToFolder: (folderId: string) => void;
  toggleDocument: (document: RawDocument) => void;
}

interface DocumentPickerListProps {
  state: DocumentPickerListState;
  actions: DocumentPickerListActions;
  versions: DocumentPickerListVersions;
  /** At a `maxSelection` cap and not already ticked: the row still shows, but
   *  its checkbox is refused. */
  isSelectionBlocked: (documentId: string) => boolean;
  /** Why a row can never be picked (e.g. it belongs to the lineage being
   *  versioned), or null. The row shows disabled with the reason beside it. */
  unselectableReasonFor?: (document: RawDocument) => string | null;
  /** Label on already-attached rows. */
  attachedLabel: string;
  searchMatchLimit: number;
}

type RowListProps = Pick<
  DocumentPickerListProps,
  | "state"
  | "actions"
  | "versions"
  | "isSelectionBlocked"
  | "unselectableReasonFor"
  | "attachedLabel"
>;

function NoProjectSelected({ projectsFailed }: { projectsFailed: boolean }) {
  const { t } = useLanguage();
  return (
    <div
      className={cn(
        "flex h-full items-center justify-center px-4 text-center text-sm",
        projectsFailed ? "text-destructive" : "text-muted-foreground"
      )}
    >
      {projectsFailed ? t.documents.picker.projectsFailed : t.documents.picker.selectProjectPrompt}
    </div>
  );
}

function DocumentSearchStatus({ state, actions, searchMatchLimit }: DocumentPickerListProps) {
  const { t } = useLanguage();
  const copy = t.documents.picker;
  return (
    <>
      <div className="relative mb-2">
        <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder={copy.filterPlaceholder}
          value={state.searchQuery}
          onChange={(event) => actions.setSearchQuery(event.target.value)}
          className="h-9 pl-8 text-sm"
        />
      </div>
      {state.subfolderDocumentsFailed && !state.documentsFailed && (
        <RetryBanner
          message={copy.subfolderSearchFailed}
          onRetry={actions.retrySubfolderDocuments}
        />
      )}
      {state.documentsFailed && state.filteredDocuments.length > 0 && (
        <RetryBanner message={copy.folderDocumentsFailed} onRetry={actions.retryDocuments} />
      )}
      {state.searchTruncated && !state.subfolderDocumentsFailed && (
        <div className="mb-2 rounded-md border border-muted-foreground/30 px-2 py-1.5 text-xs text-muted-foreground">
          {copy.searchTruncated.replace("{limit}", String(searchMatchLimit))}
        </div>
      )}
    </>
  );
}

function RetryBanner({ message, onRetry }: { message: string; onRetry: () => void }) {
  const { t } = useLanguage();
  return (
    <div className="mb-2 flex items-center justify-between gap-2 rounded-md border border-destructive/40 px-2 py-1.5 text-xs text-destructive">
      <span>{message}</span>
      <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={onRetry}>
        {t.documents.picker.retry}
      </Button>
    </div>
  );
}

function LoadingDocuments() {
  const { t } = useLanguage();
  return (
    <div
      className="space-y-2 px-2 py-1"
      aria-label={t.documents.picker.loadingDocuments}
      role="status"
    >
      {[0, 1, 2, 3].map((index) => (
        <div key={index} className="flex items-center gap-2">
          <Skeleton className="h-4 w-4 shrink-0 rounded" />
          <Skeleton className="h-4 flex-1" />
        </div>
      ))}
    </div>
  );
}

/**
 * One file row. `showVersion` labels a nested revision with its `v{n}`; a head
 * only carries a badge when it is v2+, since "v1" on a file with no history is
 * noise. The badge is the /documents version pill, and on a chain head it is
 * also the earlier-versions toggle.
 */
function DocumentResultRow(props: {
  document: RawDocument;
  state: DocumentPickerListState;
  onToggle: (document: RawDocument) => void;
  isSelectionBlocked: (documentId: string) => boolean;
  unselectableReasonFor?: (document: RawDocument) => string | null;
  attachedLabel: string;
  /** The earlier-versions toggle, on a row that heads a chain. */
  expand?: { expanded: boolean; onToggle: () => void };
  indented?: boolean;
  showVersion?: boolean;
}) {
  const {
    document,
    state,
    onToggle,
    isSelectionBlocked,
    unselectableReasonFor,
    attachedLabel,
    expand,
    indented = false,
    showVersion = false,
  } = props;
  const { t } = useLanguage();
  const documentId = String(document.id);
  const isSelected = state.selectedDocIds.has(documentId);
  const isAlreadyAttached = state.alreadyAttachedIds.includes(documentId);
  const unselectableReason = isAlreadyAttached ? null : (unselectableReasonFor?.(document) ?? null);
  const unselectable = unselectableReason !== null;
  const blocked = !isAlreadyAttached && (unselectable || isSelectionBlocked(documentId));
  const badge =
    showVersion || expand || (document.version ?? 1) > 1 ? (document.version ?? 1) : null;
  const isFromSubfolder =
    state.debouncedSearchQuery.trim() &&
    document.folder_id !== null &&
    String(document.folder_id) !== (state.selectedFolderId ?? "");
  const folderPath = isFromSubfolder
    ? state.folderBreadcrumbMap.get(String(document.folder_id))
    : null;

  return (
    <label
      title={unselectableReason || undefined}
      className={cn(
        "flex items-center gap-2 rounded px-2 py-1.5",
        indented && "pl-8",
        isAlreadyAttached || blocked
          ? "cursor-default opacity-60"
          : "cursor-pointer hover:bg-muted/50"
      )}
    >
      {isAlreadyAttached ? (
        <Check className="h-4 w-4 shrink-0 text-primary" />
      ) : (
        <Checkbox
          checked={isSelected && !unselectable}
          disabled={blocked}
          onCheckedChange={() => onToggle(document)}
        />
      )}
      <FileTypeIcon filename={document.filename} />
      <div className="min-w-0 flex-1">
        <span className="block truncate text-sm">{document.filename}</span>
        {folderPath && (
          <span className="block truncate text-xs text-muted-foreground">{folderPath}</span>
        )}
      </div>
      {badge != null && (
        <VersionBadge
          version={badge}
          hasHistory={!!expand}
          isExpanded={expand?.expanded}
          onClick={expand?.onToggle}
          ariaLabel={
            expand
              ? expand.expanded
                ? t.documents.picker.hideEarlierVersions
                : t.documents.picker.showEarlierVersions
              : undefined
          }
          filled
        />
      )}
      {isAlreadyAttached && (
        <span className="text-[10px] text-muted-foreground">{attachedLabel}</span>
      )}
      {unselectableReason && (
        <span className="max-w-[40%] shrink-0 truncate text-[10px] text-muted-foreground">
          {unselectableReason}
        </span>
      )}
    </label>
  );
}

function DocumentResults({
  state,
  actions,
  versions,
  isSelectionBlocked,
  unselectableReasonFor,
  attachedLabel,
}: RowListProps) {
  const { t } = useLanguage();
  return (
    <div className="space-y-0.5">
      {state.filteredChildFolders.map((folder) => (
        <div
          key={`folder-${folder.id}`}
          className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 hover:bg-muted/50"
          onClick={() => actions.navigateToFolder(folder.id)}
        >
          {isBriefFolder(folder) ? (
            <ClipboardList className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
          ) : isIssueAttachmentsFolder(folder) ? (
            <ListTodo className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
          ) : (
            <FolderIcon className="h-4 w-4 shrink-0 text-primary" />
          )}
          <span className="truncate text-sm font-medium">{folderLabel(folder)}</span>
        </div>
      ))}
      {state.filteredDocuments.map((document) => {
        const expandable = versions.canExpand(document);
        const expanded = expandable && versions.isExpanded(document);
        const older = expanded ? versions.older(document) : undefined;
        return (
          <div key={document.id}>
            <DocumentResultRow
              document={document}
              state={state}
              onToggle={actions.toggleDocument}
              isSelectionBlocked={isSelectionBlocked}
              unselectableReasonFor={unselectableReasonFor}
              attachedLabel={attachedLabel}
              expand={
                expandable ? { expanded, onToggle: () => versions.toggle(document) } : undefined
              }
            />
            {/* Not gated on `expanded`: the row only flips open once the fetch
                resolves, so gating here would render the spinner exactly when
                there is nothing left to wait for — the toggle looked dead for
                the whole round trip. */}
            {versions.isLoading(document) && (
              <p className="py-1 pl-8 text-xs text-muted-foreground">
                {t.documents.picker.loadingEarlierVersions}
              </p>
            )}
            {expanded &&
              older !== undefined &&
              (older.length === 0 ? (
                // The head is v2+ so predecessors existed, but they have since
                // been removed. Say so — an empty tray reads as a broken control.
                <p className="py-1 pl-8 text-xs text-muted-foreground">
                  {t.documents.picker.noEarlierVersions}
                </p>
              ) : (
                older.map((olderVersion) => (
                  <DocumentResultRow
                    key={olderVersion.id}
                    document={olderVersion}
                    state={state}
                    onToggle={actions.toggleDocument}
                    isSelectionBlocked={isSelectionBlocked}
                    unselectableReasonFor={unselectableReasonFor}
                    attachedLabel={attachedLabel}
                    indented
                    showVersion
                  />
                ))
              ))}
          </div>
        );
      })}
    </div>
  );
}

function DocumentRows(props: RowListProps) {
  const { state, actions } = props;
  const { t } = useLanguage();
  const hasContent = state.filteredChildFolders.length > 0 || state.filteredDocuments.length > 0;

  if (state.documentsFailed && state.filteredDocuments.length === 0) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 px-4 text-center text-sm text-destructive">
        {t.documents.picker.documentsFailed}
        <Button variant="outline" size="sm" onClick={actions.retryDocuments}>
          {t.documents.picker.tryAgain}
        </Button>
      </div>
    );
  }
  if (state.listLoading && !hasContent) return <LoadingDocuments />;
  if (!hasContent) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
        {t.documents.picker.emptyFolder}
      </div>
    );
  }
  return <DocumentResults {...props} />;
}

export function DocumentPickerList(props: DocumentPickerListProps) {
  if (!props.state.selectedProjectId) {
    return <NoProjectSelected projectsFailed={props.state.projectsFailed} />;
  }
  return (
    <>
      <DocumentSearchStatus {...props} />
      <div className="flex-1 overflow-y-auto">
        <DocumentRows
          state={props.state}
          actions={props.actions}
          versions={props.versions}
          isSelectionBlocked={props.isSelectionBlocked}
          unselectableReasonFor={props.unselectableReasonFor}
          attachedLabel={props.attachedLabel}
        />
      </div>
    </>
  );
}
